import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import InlineBoldText from '@/components/atoms/InlineBoldText/InlineBoldText';
import StatusBadge from '@/components/atoms/StatusBadge/StatusBadge';
import ContentBlock from '@/components/molecules/ContentBlock';
import EmptyState from '@/components/molecules/EmptyState';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import type { NewReport } from '@/domain/harvestReports';
import TwoStepDeletionButton from '@/routes/Client/Cage/pages/Reports/shared/TwoStepDeletionButton';
import { formattedDay, formattedTs } from '@/utils/date';

import { CreateHarvestReportModal } from './CreateHarvestReportModal';

import styles from './HarvestReportList.module.scss';

const ROW_HEIGHT = 52;
const SKELETON_HEIGHT = ROW_HEIGHT * 2;

export interface HarvestReport {
    id: number;
    status: string;
    updatedAt: Date | null;
    lastFeedDay: Date | null;
    slaughterDay: Date | null;
    isDraft?: boolean;
    onClick?: () => void;
    onDelete?: () => void;
}

interface HarvestReportListProps {
    isLoading?: boolean;
    reports: HarvestReport[];
    hasDrafts: boolean;
    showDraft: boolean;
    onToggleShowDraft: () => void;
    onCreateReport: (requestBody: NewReport) => Promise<void>;
}

export default function HarvestReportList(props: HarvestReportListProps) {
    const { reports, isLoading, hasDrafts, showDraft, onToggleShowDraft, onCreateReport } = props;
    const { t } = useTranslation(['harvestReport', 'common']);

    const columns: DataTableColumnDef<HarvestReport>[] = [
        {
            id: 'status',
            header: t('harvestReport:reportList.status'),
            cell: ({ row: { original: report } }) => (
                <StatusBadge
                    className={styles.statusBadge}
                    status={report.status}
                    isDraft={report.isDraft}
                    draftLabel={
                        report.isDraft ? t('harvestReport:common.draftBadgeLabel') : undefined
                    }
                />
            ),
        },
        {
            id: 'lastFeedDay',
            header: t('harvestReport:common.lastFeedDay'),
            cell: ({ row }) => formattedDay(row.original.lastFeedDay),
        },
        {
            id: 'slaughterDay',
            header: t('harvestReport:common.harvestDate'),
            cell: ({ row }) => formattedDay(row.original.slaughterDay) ?? '-',
        },
        {
            id: 'updatedAt',
            header: t('harvestReport:reportList.updatedAt'),
            cell: ({ row }) => formattedTs(row.original.updatedAt),
        },
        {
            id: 'actions',
            header: t('harvestReport:reportList.actions'),
            cell: ({ row: { original: report } }) => (
                <div className={styles.actionsContainer}>
                    <Button
                        onClick={() => report.onClick?.()}
                        variant="table"
                        trackingId="harvest_report_show"
                        disabled={report.status !== 'finished' && report.status !== 'warning'}
                        size={'medium'}>
                        {t('common:actions.show')}
                    </Button>
                    <TwoStepDeletionButton onConfirm={() => report.onDelete?.()} />
                </div>
            ),
        },
    ];

    return (
        <ContentBlock>
            <CreateHarvestReportModal onSubmitted={onCreateReport} />
            <DataTable
                data={reports}
                columns={columns}
                getRowId={(report) => String(report.id)}
                layout="grid"
                enableSorting={false}
                rowHeight={ROW_HEIGHT}
                isLoading={isLoading}
                skeletonHeight={SKELETON_HEIGHT}
                emptyState={
                    <EmptyState
                        appearance="quiet"
                        size="spacious"
                        title={<InlineBoldText text={t('harvestReport:reportList.emptyList')} />}
                    />
                }
            />
            {hasDrafts && (
                <Button
                    className={styles.showDraftButton}
                    size={'large'}
                    variant={'secondary'}
                    trackingId="harvest_report_toggle_draft"
                    onClick={onToggleShowDraft}>
                    {showDraft
                        ? t('harvestReport:common.hideDraft')
                        : t('harvestReport:common.showDraft')}
                </Button>
            )}
        </ContentBlock>
    );
}
